// Universal config-driven matter dashboard.
//
// One renderer for every practice area. Reads `template.ui.widgets[]` from
// the practice-area template (fetched by id from the backend YAML registry)
// and renders each widget through the registry in `./widgets/index.ts`.
//
// Composition (top to bottom): MatterHero, AISummary, the widget grid
// (config-driven cards, 12-col, widget colSpan default 4), SwimLanes, the
// panels the modules register, ClientAccessCard, DocWall.
//
// When `template` is null/undefined (matter has no `practiceAreaTemplateId`,
// or the API returns 404), MatterView falls back to the legacy
// PracticeAreaDispatcher path and never renders this component.

import type { Client, Matter, PracticeArea } from "../../data/types";
import type { PracticeAreaTemplate, WidgetConfig } from "../../hooks/usePracticeAreaTemplate";
import { AISummary } from "./AISummary";
import { MatterHero } from "./MatterHero";
import { useScreens } from "../chrome/screens";
import { ClientAccessCard } from "./ClientAccessCard";
import { DocWall } from "./DocWall";
import { SwimLanes } from "./SwimLanes";
import { WIDGETS } from "./widgets";
import { WidgetCard } from "./widgets/WidgetCard";
import type { MatterWithClient } from "../../api/mock";

interface Props {
  matter: Matter;
  client: Client | null | undefined;
  pa: PracticeArea | null;
  template: PracticeAreaTemplate;
  templateId: string | null;
}

function gridColumn(colSpan: number | undefined): string {
  // 12-col grid; widget defaults to 4 cols (3-up). Clamp to [3, 12].
  const n = Math.max(3, Math.min(12, colSpan ?? 4));
  return `span ${n} / span ${n}`;
}

export function MatterDashboard({ matter, client, pa, template, templateId }: Props) {
  const widgets = (template.ui?.widgets as WidgetConfig[] | undefined) ?? [];
  const caseId = (matter as Partial<MatterWithClient>).mongoId;
  const panels = useScreens().flatMap((s) =>
    (s.matterPanels ?? []).map((Panel, i) => ({ key: `${s.id}-${i}`, Panel })),
  );

  return (
    <>
      <MatterHero matter={matter} client={client} pa={pa} templateId={templateId ?? template.id} />
      <AISummary summary={matter.aiSummary} caseId={caseId} />

      {widgets.length > 0 && (
        // Grid styles live in styles/polish.css (.cc-widget-grid) and the
        // mid-screen reflow lives in responsive.css. Keep this JSX clean so
        // the layout stays a function of CSS, not inline overrides.
        <div className="cc-widget-grid">
          {widgets.map((w, i) => {
            const Comp = WIDGETS[w.type];
            // Track the requested colSpan as a CSS var so the 1024px reflow
            // rule can clamp without re-reading the inline style.
            const colSpan = Math.max(3, Math.min(12, (w.colSpan as number | undefined) ?? 4));
            return (
              <div
                key={`${w.type}-${w.key ?? i}`}
                className="cc-widget-cell"
                style={{
                  gridColumn: gridColumn(colSpan),
                  ["--ws" as string]: String(colSpan),
                }}
              >
                {Comp ? (
                  <Comp config={w} matter={matter} client={client} template={template} />
                ) : (
                  <WidgetCard title={w.title ?? w.type} sub="not implemented">
                    <div style={{ fontSize: 12.5, color: "var(--muted)" }}>
                      Widget type <code>{w.type}</code> is referenced by <code>{template.id}</code>{" "}
                      but not yet registered.
                    </div>
                  </WidgetCard>
                )}
              </div>
            );
          })}
        </div>
      )}

      <SwimLanes matter={matter} pa={pa} />
      {/* Keyed by the matter: these cards hold typed input and result
          notes, and switching to an already-cached matter re-renders them
          without unmounting - one client's date of birth would arrive on
          another's card. */}
      {panels.map(({ key, Panel }) => (
        <Panel key={`${key}-${caseId ?? ""}`} caseId={caseId} clientEmail={client?.contactEmail} />
      ))}
      <ClientAccessCard key={`access-${caseId ?? ""}`} caseId={caseId} />
      <DocWall documents={matter.documents} caseId={caseId} driveFolder={matter.driveFolder} />
    </>
  );
}
